// =============================================================================
// Sheet lateral para seleccionar / crear cliente desde el POS. Slide-in desde
// la derecha, secciones plegables — patrón Alegra/Defontana adaptado a Panamá:
// RUC + DV, tipo de receptor DGI, provincia/distrito/corregimiento, segmento.
// =============================================================================
import { useEffect, useState } from "react";
import { crm, type Customer } from "../lib/api";

// Tipos de identificación válidos en Panamá (factura electrónica DGI/SFEP).
const ID_TYPES = [
  { value: "cedula", label: "Cédula de identidad" },
  { value: "ruc", label: "RUC (contribuyente)" },
  { value: "passport", label: "Pasaporte" },
  { value: "nit_foreign", label: "NIT extranjero" },
] as const;

// tipoReceptor DGI 1..4 + locales. Determina qué se exige fiscalmente.
const RECEIVER_TYPES = [
  { value: "consumidor_final", label: "Consumidor final" },
  { value: "contribuyente",   label: "Contribuyente (con RUC)" },
  { value: "gobierno",        label: "Gobierno" },
  { value: "extranjero",      label: "Extranjero / no domiciliado" },
  { value: "zona_franca",     label: "Zona Franca / Panamá Pacífico" },
] as const;

const SEGMENTS = [
  { value: "minorista", label: "Minorista" },
  { value: "obra",      label: "Obra / Contratista" },
  { value: "distribuidor", label: "Distribuidor" },
  { value: "gobierno",  label: "Gobierno" },
] as const;

// Las 10 provincias + las 3 comarcas indígenas de Panamá.
const PROVINCIAS = [
  "Bocas del Toro", "Coclé", "Colón", "Chiriquí", "Darién", "Herrera",
  "Los Santos", "Panamá", "Panamá Oeste", "Veraguas",
  "Comarca Emberá-Wounaan", "Comarca Guna Yala", "Comarca Ngäbe-Buglé",
] as const;

/**
 * Sheet de alta de cliente — uso normal: se abre directo desde el botón "NUEVO"
 * del combobox del POS. No incluye lista (la lista vive en el combobox).
 */
export function NewCustomerSheet({ onCreated, onCancel }: { onCreated: (c: Customer) => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onCancel}>
      <aside onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl animate-slide-in-right">
        <header className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-grafito-100 text-lg">👤</span>
            <div>
              <h2 className="text-base font-bold">Nuevo cliente</h2>
              <p className="text-xs text-grafito-400">Crea los contactos que asociarás en tus facturas de venta.</p>
            </div>
          </div>
          <button onClick={onCancel} aria-label="Cerrar" className="text-2xl leading-none text-grafito-400 hover:text-grafito-700">×</button>
        </header>
        <NewCustomerForm onCancel={onCancel} onCreated={onCreated} />
      </aside>
    </div>
  );
}

/**
 * CustomerPicker antiguo: lista + form (kept for backwards compat, no se usa
 * en POS — ahora se usa CustomerCombobox + NewCustomerSheet).
 */
export function CustomerPicker({
  value, requireForCredit = false,
  onPick, onCancel,
}: {
  value: Customer | null;
  requireForCredit?: boolean;
  onPick: (c: Customer | null) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    crm.listCustomers(q).then((rs) => { if (!cancelled) setItems(rs); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onCancel}>
      <aside onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl animate-slide-in-right">
        <header className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-grafito-100 text-lg">👤</span>
            <div>
              <h2 className="text-base font-bold">{showNew ? "Nuevo cliente" : "Seleccionar cliente"}</h2>
              <p className="text-xs text-grafito-400">
                {showNew ? "Crea el contacto que asociarás a tus facturas y a su cuenta de crédito." : "Busca por nombre o RUC, o crea un cliente nuevo."}
              </p>
            </div>
          </div>
          <button onClick={onCancel} aria-label="Cerrar" className="text-2xl leading-none text-grafito-400 hover:text-grafito-700">×</button>
        </header>

        {!showNew ? (
          <PickList q={q} setQ={setQ} items={items} loading={loading} value={value}
            onPick={onPick} onNew={() => setShowNew(true)} requireForCredit={requireForCredit} />
        ) : (
          <NewCustomerForm onCancel={() => setShowNew(false)} onCreated={onPick} />
        )}
      </aside>
    </div>
  );
}

// ---- Vista lista del sheet ---------------------------------------------
function PickList({ q, setQ, items, loading, value, onPick, onNew, requireForCredit }: {
  q: string; setQ: (v: string) => void;
  items: Customer[]; loading: boolean;
  value: Customer | null;
  onPick: (c: Customer | null) => void;
  onNew: () => void;
  requireForCredit: boolean;
}) {
  return (
    <>
      <div className="border-b px-6 py-3">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="🔎 Buscar por nombre o RUC…"
          className="w-full rounded-xl border px-4 py-3 text-base outline-none focus:border-brand" />
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="rounded-xl border">
          {loading && <div className="p-4 text-center text-sm text-grafito-400">Cargando…</div>}
          {!loading && items.length === 0 && <div className="p-4 text-center text-sm text-grafito-400">Sin resultados.</div>}
          {items.map((c) => (
            <button key={c.id} onClick={() => onPick(c)}
              className="flex w-full items-center justify-between border-b px-4 py-3 text-left last:border-0 hover:bg-grafito-50">
              <div className="min-w-0">
                <div className="truncate font-semibold">{c.name}</div>
                <div className="text-xs text-grafito-400">
                  {c.ruc ? `RUC ${c.ruc}-${c.dv ?? ""}` : "Sin RUC"} {c.phone && `· ${c.phone}`}
                  {c.commercial_segment && c.commercial_segment !== "minorista" && (
                    <span className="ml-1 rounded bg-brand/10 px-1 text-[10px] font-semibold uppercase tracking-wide text-brand">
                      {c.commercial_segment}
                    </span>
                  )}
                </div>
              </div>
              {value?.id === c.id && <span className="text-brand">✓</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="border-t bg-white px-6 py-4">
        <button onClick={onNew} className="btn btn-primary w-full">+ Crear cliente nuevo</button>
        {!requireForCredit && (
          <button onClick={() => onPick(null)} className="btn btn-ghost mt-2 w-full">Continuar como consumidor final</button>
        )}
        {requireForCredit && (
          <p className="mt-2 text-center text-xs text-amber-700">El fiado requiere un cliente con cuenta de crédito.</p>
        )}
      </div>
    </>
  );
}

// ---- Formulario serio: cubre lo que pide la DGI Panamá + datos comerciales --
function NewCustomerForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: Customer) => void }) {
  const [idType, setIdType] = useState<typeof ID_TYPES[number]["value"]>("cedula");
  const [identification, setIdentification] = useState("");
  const [dv, setDv] = useState("");
  const [receiverType, setReceiverType] = useState<typeof RECEIVER_TYPES[number]["value"]>("consumidor_final");
  const [name, setName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [segment, setSegment] = useState<typeof SEGMENTS[number]["value"]>("minorista");
  const [provincia, setProvincia] = useState("Panamá");
  const [distrito, setDistrito] = useState("");
  const [corregimiento, setCorregimiento] = useState("");
  const [address, setAddress] = useState("");
  const [phones, setPhones] = useState<string[]>([""]);
  const [email, setEmail] = useState("");
  const [paymentTermsDays, setPaymentTermsDays] = useState("0");
  const [creditLimit, setCreditLimit] = useState("0");
  const [notes, setNotes] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const requiresRucDv = receiverType === "contribuyente" || receiverType === "gobierno";

  async function submit() {
    if (!name.trim()) { setErr("La razón social / nombre es obligatorio."); return; }
    if (requiresRucDv && (!identification.trim() || !dv.trim())) {
      setErr("Un contribuyente debe tener RUC + DV (necesario para la factura electrónica DGI).");
      return;
    }
    if (!address.trim()) { setErr("La dirección es obligatoria para emitir factura."); return; }

    setBusy(true); setErr(null);
    try {
      const fullAddress = [address, corregimiento, distrito, provincia].filter(Boolean).join(", ");
      const c = await crm.upsertCustomer({
        name: name.trim(),
        taxReceiverType: receiverType,
        commercialSegment: segment,
        ruc: idType === "ruc" ? identification.trim() : undefined,
        dv: idType === "ruc" ? dv.trim() : undefined,
        cedula: idType === "cedula" ? identification.trim() : undefined,
        phone: phones.find((p) => p.trim()) || undefined,
        email: email.trim() || undefined,
        address: fullAddress || undefined,
      });
      onCreated(c as Customer);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {err && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {/* DATOS GENERALES — todo el contacto en una sola sección, como Alegra */}
        <Section title="Datos generales" subtitle="Ingresa la información principal de tu cliente." defaultOpen>
          <Row>
            <Select label="Tipo de identificación *" value={idType} onChange={(v) => setIdType(v as typeof idType)}
              options={ID_TYPES.map((t) => ({ value: t.value, label: t.label }))} />
            <Field label={idType === "ruc" ? "RUC *" : "Identificación *"} value={identification} onChange={setIdentification}
              placeholder={idType === "ruc" ? "155555000-2-2019" : "8-NNN-NNNN"} />
          </Row>
          {idType === "ruc" && (
            <Field label="DV (dígito verificador) *" value={dv} onChange={setDv} placeholder="86" max={3} />
          )}
          <Row>
            <Field label="Nombre / Razón social *" value={name} onChange={setName} placeholder='"Constructora RG, S.A." o "Juan Pérez"' />
            <Field label="Nombre comercial" value={tradeName} onChange={setTradeName} placeholder="Si difiere del legal" />
          </Row>
          <Row>
            <Select label="Tipo de receptor (DGI)" value={receiverType} onChange={(v) => setReceiverType(v as typeof receiverType)}
              options={RECEIVER_TYPES.map((r) => ({ value: r.value, label: r.label }))} />
            <Select label="Segmento comercial" value={segment} onChange={(v) => setSegment(v as typeof segment)}
              options={SEGMENTS.map((s) => ({ value: s.value, label: s.label }))} />
          </Row>
          <Row>
            <Select label="Provincia / Distrito *" value={provincia} onChange={setProvincia}
              options={PROVINCIAS.map((p) => ({ value: p, label: p }))} />
            <Field label="Distrito" value={distrito} onChange={setDistrito} placeholder="Ej: David" />
          </Row>
          <Row>
            <Field label="Corregimiento" value={corregimiento} onChange={setCorregimiento} placeholder="Ej: David Centro" />
            <Field label="Dirección detallada *" value={address} onChange={setAddress} placeholder="Calle / barrio / nro. / referencia" />
          </Row>
          <Row>
            <Field label="Correo" value={email} onChange={setEmail} type="email" placeholder="cliente@correo.pa" />
            <div>
              <span className="mb-1 block text-xs font-medium text-grafito-500">Teléfono</span>
              <div className="flex items-center gap-1">
                <input value={phones[0] ?? ""} onChange={(e) => setPhones((c) => { const cp = [...c]; cp[0] = e.target.value; return cp; })}
                  placeholder="6677-8899"
                  className="flex-1 rounded-lg border px-3 py-2 outline-none focus:border-brand" />
                <button type="button" onClick={() => setPhones((c) => [...c, ""])}
                  title="Agregar otro teléfono"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-lg font-bold text-brand hover:bg-brand/10">+</button>
              </div>
            </div>
          </Row>
          {phones.slice(1).map((p, idx) => {
            const i = idx + 1;
            return (
              <div key={i} className="flex items-end gap-2">
                <div className="flex-1">
                  <Field label={`Teléfono ${i + 1}`} value={p}
                    onChange={(v) => setPhones((c) => c.map((x, j) => (j === i ? v : x)))}
                    placeholder="6677-8899" />
                </div>
                <button type="button" onClick={() => setPhones((c) => c.filter((_, j) => j !== i))}
                  className="mb-2 text-grafito-400 hover:text-red-600">✕</button>
              </div>
            );
          })}
        </Section>

        {/* INFO DE VENTA */}
        <Section title="Información de venta" subtitle="Plazo de pago, lista de precios y cupo de crédito.">
          <Row>
            <Field label="Plazo de pago (días)" value={paymentTermsDays} onChange={setPaymentTermsDays}
              placeholder="0 = contado" type="number" />
            <Field label="Cupo de crédito (B/.)" value={creditLimit} onChange={setCreditLimit}
              placeholder="0.00" type="number" />
          </Row>
          <p className="text-xs text-grafito-400">
            La lista de precios (Constructor, Mayorista, etc.) y el cupo definitivo se asignan desde la ficha del cliente.
          </p>
        </Section>

        {/* NOTAS */}
        <Section title="Notas internas" subtitle="Solo visibles para tu equipo (no se imprimen).">
          <label className="block">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder='Ej: "Pide siempre cemento extra fino"'
              className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
          </label>
        </Section>

        <p className="mt-2 text-xs text-grafito-400">* Campos obligatorios</p>
      </div>

      <div className="flex gap-2 border-t bg-white px-6 py-4">
        <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
        <button onClick={submit} disabled={busy} className="btn btn-primary flex-1 disabled:opacity-40">
          {busy ? "Creando…" : "Crear y elegir"}
        </button>
      </div>
    </>
  );
}

// ---- Subcomponentes de form ---------------------------------------------
function Section({ title, subtitle, children, defaultOpen = false }: { title: string; subtitle?: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-3 overflow-hidden rounded-xl border bg-white">
      <button onClick={() => setOpen(!open)} type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-grafito-50">
        <div>
          <div className="text-sm font-bold text-brand">{title}</div>
          {subtitle && <div className="text-xs text-grafito-400">{subtitle}</div>}
        </div>
        <span className={`text-grafito-400 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && <div className="space-y-3 border-t px-4 py-4">{children}</div>}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function Field({ label, value, onChange, autoFocus, placeholder, type = "text", max }: {
  label: string; value: string; onChange: (v: string) => void;
  autoFocus?: boolean; placeholder?: string; type?: string; max?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-grafito-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus} placeholder={placeholder} maxLength={max}
        className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
    </label>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-grafito-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border bg-white px-3 py-2 outline-none focus:border-brand">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
